import axios from 'axios';
import { auth } from '../config/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const getHeaders = async () => {
    const token = await auth.currentUser?.getIdToken();
    return {
        headers: {
            Authorization: `Bearer ${token}`
        }
    };
};

export interface ReportFilters {
    startDate?: string;
    endDate?: string;
    marca?: string;
    modelo?: string;
    talla?: string;
    color?: string;
    vendedora?: string;
    cliente?: string;
    metodoPago?: string;
    estado?: string;
}

export type DateRangeParams = ReportFilters;

export interface FilterOptions {
    marcas: string[];
    tallas: string[];
    colores: string[];
    vendedoras: string[];
}

export const getFilterOptions = async (): Promise<FilterOptions> => {
    const headers = await getHeaders();
    const response = await axios.get(`${API_URL}/reports/filter-options`, headers);
    return response.data;
};

export interface SalesMetric {
    totalSales: number;
    totalRevenue: number;
    averageTicket: number;
    totalDiscounts?: number;
    discountedSalesCount?: number;
    preOrdersThisMonthCount?: number;
    capitalCommitted?: number;
}

export interface ChartData {
    date: string;
    total: number;
}

export interface TopModel {
    code: string;
    name: string;
    quantity: number;
    revenue: number;
}

export interface TopVendor {
    name: string;
    total: number;
}

export interface SalesReport {
    metrics: SalesMetric;
    charts: {
        salesByDate: ChartData[];
        topModels: TopModel[];
        topVendors: TopVendor[];
    };
}

export interface InventoryReport {
    metrics: {
        totalProducts: number;
        valueVariables: number;
        totalStock: number;
        zeroStockCount: number;
        lowStockCount: number;
        activePreOrderProductsCount?: number;
        distinctPreOrderProductsCount?: number;
    };
    lists: {
        lowStock: any[];
        topMoving: any[];
        slowMoving: any[];
    };
}

export const reportService = {
    // Legacy Metrics
    getGlobalMetrics: async () => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/global-metrics`, headers);
        return response.data;
    },

    // Detailed Lists
    getReportSales: async (params: DateRangeParams) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/list/sales`, { ...headers, params });
        return response.data;
    },

    getReportPreOrders: async (params: DateRangeParams & { status?: string }) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/list/preorders`, { ...headers, params });
        return response.data;
    },

    getReportLayaways: async (params: ReportFilters) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/list/layaways`, { ...headers, params });
        return response.data;
    },

    getReportInvoices: async (params: DateRangeParams) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/list/invoices`, { ...headers, params });
        return response.data;
    },

    getReportPayments: async (params: DateRangeParams) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/list/payments`, { ...headers, params });
        return response.data;
    },

    getReportCommissions: async (params: DateRangeParams & { status?: string }) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/list/commissions`, { ...headers, params });
        return response.data;
    },

    // Analytics Visual Reports
    getSalesMetrics: async (params: ReportFilters) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/sales`, { ...headers, params });
        return response.data;
    },

    getInventoryMetrics: async (params: { type?: string } & ReportFilters) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/inventory`, { ...headers, params });
        return response.data;
    },

    // New API analytics
    getSalesAnalytics: async (params: ReportFilters) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/analytics/sales`, { ...headers, params });
        return response.data;
    },

    getInventoryAnalytics: async (params: ReportFilters) => {
        const headers = await getHeaders();
        const response = await axios.get(`${API_URL}/reports/analytics/inventory`, { ...headers, params });
        return response.data;
    }
};
